import { ls } from './utility/local-storage';
import { ErrorNotifier } from './error-notifier';
import { TriggeredEvent } from './triggered-event.types';
import { isMalformedJson } from './utility/malformed-json-detector';
import { console } from './utility/console';

const OLDEST_EVENT_LOG_ENTRY_HOURS_BEFORE = 3;
const LIMIT_COUNT_BY_EVENT = 30;
const KEY_PREFIX = 'rpr_el_v1';

const oldestEntryTimestamp = (limit_hours: number): Date => {
  const date = new Date();
  date.setHours(date.getHours() - limit_hours);
  return date;
};

type DecodedTriggeredEventRecord = Omit<TriggeredEvent, 'tracked_at'> & {
  // 一度 storage に永続化した段階で tracked_at は文字列化されているので、
  // TriggeredEvent のうち tracked_at の型定義だけを上書きする
  tracked_at: string;
};

export interface EventLogStorage {
  append(event: TriggeredEvent): Promise<void>;
  fetch(eventName: string): Promise<TriggeredEvent[]>;
  compact(limitHours: number): Promise<void>;
}

// DESIGN
// key: `${KEY_PREFIX}:${userId}:${eventName}`
// value: [JSON object by event]
export class LocalStorageEventLogStorage implements EventLogStorage {
  private keyBase: string;

  constructor(userIdentifier: string) {
    this.keyBase = `${KEY_PREFIX}:${userIdentifier}`;
    this.compact();
  }

  public fetch(eventName: string): Promise<TriggeredEvent[]> {
    const fullKey = this.getKeyName(eventName);
    return Promise.resolve(this.getEventLogsByFullKey(fullKey));
  }

  // TODO:
  // 顧客の実装ミスで高頻度に呼ばれるとCPUを食いやすい
  // ある程度memoryでbufferする実装もいいかも
  public append(event: TriggeredEvent): Promise<void> {
    const events = this.getEventLogsByFullKey(this.getKeyName(event.name));
    events.push(event);
    // 利用容量を制限するため、古いデータは捨てる
    const limitedEvents = events.slice(-LIMIT_COUNT_BY_EVENT);

    if (isMalformedJson(limitedEvents)) {
      const error = new Error('Saving event log skipped due to JSON.stringify is broken.');
      console.warn(error);
      // prototype.jsの特定バージョンのせいでJsonが壊れるケースを検知している
      // 発生時の調査を楽にするためprototype.jsがサイトで読み込まれているかどうかの情報を取得する
      // @ts-ignore
      const prototypejsObject = typeof window.Prototype;
      // @ts-ignore
      const typeofToJSON = typeof Array.prototype.toJSON;
      ErrorNotifier.notify({
        error,
        extraPayload: {
          prototypejsUrls: [...document.scripts].filter((s) => s.src.match('prototype.js')).map((s) => s.src),
          event,
          typeofToJSON,
          prototypejsObject,
        },
        rollbarOptions: {
          fingerprint: `json-broken-${document.domain}`,
        },
        samplingRatio: 10000,
      });
      return Promise.resolve();
    }

    try {
      ls.set(this.getKeyName(event.name), JSON.stringify(limitedEvents));
      return Promise.resolve();
    } catch (error: any) {
      if (error.name === 'QuotaExceededError') {
        // QuotaExceededError=容量が一杯になったら全logを削除する
        this.compact(-1);
        return Promise.resolve();
      }
      ErrorNotifier.notify({
        error,
        extraPayload: {
          errorName: error.name,
          event,
        },
      });
      return Promise.reject(error);
    }
  }

  public compact(limitHours: number = OLDEST_EVENT_LOG_ENTRY_HOURS_BEFORE): Promise<void> {
    const limitTime = new Date(oldestEntryTimestamp(limitHours)).getTime();
    const reproEventLogKeys = Object.keys(localStorage).filter((key) => key.match(new RegExp(`^${KEY_PREFIX}`)));

    reproEventLogKeys.forEach((key) => {
      const events = this.getEventLogsByFullKey(key);
      const storingEvents = events.filter((event) => event.tracked_at.getTime() >= limitTime);
      if (storingEvents.length === 0) {
        ls.remove(key);
      } else if (storingEvents.length !== events.length) {
        ls.set(key, JSON.stringify(storingEvents));
      }
    });
    return Promise.resolve();
  }

  private getEventLogsByFullKey(keyName: string): TriggeredEvent[] {
    const recordsJson = ls.get(keyName);
    if (recordsJson === null) return [];
    let records = [];
    try {
      records = JSON.parse(recordsJson);
    } catch (error: any) {
      ErrorNotifier.notify({
        error,
        extraPayload: {
          recordsJson,
        },
        rollbarOptions: {
          fingerprint: 'getEventLogsByFullKey-catch-error',
        },
      });
      console.warn(error);
      return [];
    }
    // prototype.js の古い特定のバージョンが Array.prototype.toJSON 関数を勝手に生やしている結果、
    // JSON.stringify(this.toJSON) を実行した際に、本来は Array な プロパティが String になってしまうことがある。
    // 通常recordsの型はTriggeredEvent[]だが、そのような場合にStringになってしまうため
    // その場合は何も何も返さないようにする
    if (typeof records === 'string') {
      return [];
    }
    return records.map((record: DecodedTriggeredEventRecord) => {
      return {
        name: record.name,
        properties: record.properties,
        tracked_at: new Date(record.tracked_at),
      };
    });
  }

  private getKeyName(eventName: string): string {
    return `${this.keyBase}:${eventName}`;
  }

  public static migrateIdfv(oldIdfv: string, newIdfv: string): void {
    const reproEventLogKeys = Object.keys(localStorage).filter((key) => key.match(new RegExp(`^${KEY_PREFIX}`)));
    reproEventLogKeys.forEach((key) => {
      if (key.includes(oldIdfv)) {
        const val = ls.get(key);
        if (val == null) return;
        ls.remove(key);
        ls.set(key.replace(oldIdfv, newIdfv), val);
      }
    });
  }
}
